import { alpha, Box, Button, Card, CardContent, Divider, SxProps, Theme, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { ICampaignItem } from '../../../types/pyg';
import { isActivationKey } from '../../../util/ui';
import LightboxBackgroundImage from './LightboxBackgroundImage';

const MAX_VISIBLE = 4;
const IMAGE_SIZE = 52;

interface IProps {
  title: string;
  items: ICampaignItem[];
  onClick: () => void;
}

export const SummaryCard: React.FC<IProps> = ({ title, items, onClick }) => {
  const theme = useTheme();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget || !isActivationKey(event)) {
        return;
      }
      event.preventDefault();
      onClick();
    },
    [onClick],
  );

  const handleActionClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  const detailsLabel = useMemo(() => {
    const name = title || (items.length === 1 ? items[0]?.name : '');

    return name ? `View details for ${name}` : 'View details for your selection';
  }, [title, items]);

  const visibleItems = useMemo(() => items.slice(0, MAX_VISIBLE), [items]);
  const overflowCount = useMemo(() => (items.length > MAX_VISIBLE ? items.length - MAX_VISIBLE : 0), [items]);

  const restItems = useMemo(() => {
    return (
      <Box
        onClick={handleActionClick}
        sx={{
          width: IMAGE_SIZE,
          height: IMAGE_SIZE,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        <Typography variant="h6" fontWeight="medium" color={theme.palette.primary.main}>
          +{overflowCount}
        </Typography>
      </Box>
    );
  }, [overflowCount, handleActionClick, theme]);

  const contentStyles: SxProps<Theme> = useMemo(() => {
    return items.length > MAX_VISIBLE
      ? {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
        }
      : {
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
        };
  }, [items.length]);

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={detailsLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        cursor: 'pointer',
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          pb: 0,
          mb: 1,
        }}
      >
        <Typography variant="body1" fontWeight="medium">
          {title}
        </Typography>
        <Button variant="text" color="primary" size="small" onClick={handleActionClick}>
          View details
        </Button>
      </Box>
      <Divider sx={{ mx: 2, mt: -2.5, mb: -0.5, borderColor: theme.palette.grey[400] }} />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          pt: 0,
          px: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        <Box sx={contentStyles}>
          {visibleItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                width: IMAGE_SIZE,
                height: IMAGE_SIZE,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
              }}
            >
              <LightboxBackgroundImage
                url={item.image_url}
                asImage
                stopPropagation
                isScalable
                sx={{ width: '100%', height: '100%' }}
              />
            </Box>
          ))}

          {overflowCount > 0 && restItems}
        </Box>
      </CardContent>
    </Card>
  );
};
